/**
 * SITREP detail view (SITREP.1.5) — a modal overlay showing one generated
 * Situation Report.
 *
 * Header: title + generation timestamp + Response Level badge. Body: the rendered
 * markdown narrative. Actions: Copy as markdown (clipboard), Download JSON
 * (structured_data), Forward to PEOC (REAL delivery via the shared webhook channel
 * — Task #79; records to delivery_log + flips status to 'forwarded'), Close. Every
 * action is logged to operator_actions (view on open, export on copy/download,
 * forward on the click).
 */
import { useEffect, useState } from 'react';
import type { SitrepReport } from '@tsr/shared-types';
import { ResponseLevelBadge } from './ResponseLevelBadge';
import { AnomalyResponseLevelBadge } from './AnomalyResponseLevelBadge';
import { renderMarkdown } from './lib/markdown';
import { forwardSitrep } from './api';
import { DestinationPicker } from './DestinationPicker';
import { getSessionId, logAction } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { FORWARDED_LOCALLY_NOTE } from './deliveryToastCopy';

function fmtCreated(iso: string): string {
  // The API serves an ISO-ish string ("YYYY-MM-DDTHH:MM:SSZ"); show date + UTC time.
  return iso.length >= 16 ? `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC` : iso;
}

export function SitrepDetailPanel({
  sitrep,
  onClose,
  docked = false,
}: {
  sitrep: SitrepReport;
  onClose: () => void;
  /** Genuinely-wide layout (#131/#192 — lib/wideDisplay.ts): render as an
   *  in-flow ~720px reading column docked beside the detail panel instead of a
   *  centred modal overlay. Same component, same actions, same audit — only the
   *  chrome (backdrop vs. column) differs. */
  docked?: boolean;
}): JSX.Element {
  const [forwarding, setForwarding] = useState(false);
  const [destinationId, setDestinationId] = useState<string | undefined>(undefined);

  // #620 — the anchored incident's IC §5 Response Level snapshot; present ONLY on an anchored
  // SITREP whose incident carried a pin at generation time. Its presence (never scope_type) is
  // the sole gate for the §5 dual-display — an un-anchored / un-pinned report shows no Response
  // Level at all (the COP never COMPUTES a §5 level; it only displays one the IC set).
  const incidentRL = sitrep.structured_data.incident_response_level ?? null;

  // Log the view once when the panel opens for this report.
  useEffect(() => {
    logAction('view_sitrep', { sitrep_id: sitrep.id, response_level_max: sitrep.response_level_max });
  }, [sitrep.id, sitrep.response_level_max]);

  // Esc closes the modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyMarkdown = (): void => {
    logAction('export_sitrep_markdown', { sitrep_id: sitrep.id, format: 'clipboard' });
    const done = (): void => {
      showToast('SITREP copied as markdown.', { variant: 'success' });
    };
    const fail = (): void => {
      showToast('Could not copy to clipboard.', { variant: 'error' });
    };
    const clip = (navigator as { clipboard?: { writeText?: (t: string) => Promise<void> } }).clipboard;
    if (clip?.writeText) {
      void clip.writeText(sitrep.summary_markdown).then(done).catch(fail);
    } else {
      fail();
    }
  };

  const downloadJson = (): void => {
    logAction('export_sitrep_markdown', { sitrep_id: sitrep.id, format: 'json' });
    try {
      const blob = new Blob([JSON.stringify(sitrep.structured_data, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sitrep-${sitrep.id.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('SITREP structured data downloaded.', { variant: 'success' });
    } catch {
      showToast('Could not download the JSON.', { variant: 'error' });
    }
  };

  const forward = (): void => {
    // Real delivery (Task #79): the server forwards via the shared webhook channel,
    // records the attempt to delivery_log, and logs `forward_sitrep_clicked`.
    if (forwarding) return;
    setForwarding(true);
    void forwardSitrep(sitrep.id, getSessionId(), destinationId)
      .then((res) => {
        const dest = res.destination?.name ?? 'PEOC duty officer';
        if (res.delivered && res.delivery_channel === 'webhook') {
          showToast(
            `Forwarded to ${dest} via webhook (delivery #${res.delivery_log_id.slice(0, 8)}).`,
            { variant: 'success' },
          );
        } else if (res.delivered) {
          showToast(`Forwarded to ${dest} (${FORWARDED_LOCALLY_NOTE}).`, { variant: 'info' });
        } else {
          showToast(`Forwarding failed: ${res.error ?? 'delivery error'}.`, { variant: 'error' });
        }
      })
      .catch(() => showToast('Could not forward the SITREP. Please retry.', { variant: 'error' }))
      .finally(() => setForwarding(false));
  };

  const card = (
    <div
      data-testid="sitrep-modal"
      onClick={docked ? undefined : (e) => e.stopPropagation()}
      className={
        docked
          ? 'flex h-full w-[var(--sitrep-dock-w)] shrink-0 flex-col border-l border-slate-700 bg-slate-900 text-slate-100'
          : 'flex flex-col w-full max-w-2xl max-h-[85vh] rounded-lg border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl'
      }
    >
        <header className="flex items-start justify-between gap-3 px-5 py-3 border-b border-slate-700">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold tracking-tight truncate" data-testid="sitrep-title">
              {sitrep.title}
            </h2>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] text-slate-400">{fmtCreated(sitrep.created_at)}</span>
              {/* #613/#620 — WITHOUT an anchored IC §5 pin, the SITREP has no assessed Response
                  Level to show; the header carries only the machine roll-up (response_level_max),
                  which is the Threat axis (region/community/incident/un-pinned reports). */}
              {!incidentRL && (
                <ResponseLevelBadge level={sitrep.response_level_max} axis="threat" />
              )}
              <span className="text-[10px] uppercase tracking-wide text-slate-500">
                {sitrep.status}
              </span>
            </div>
            {/* #620 — DUAL DISPLAY, anchored+pin ONLY: the IC-assessed §5 Response Level is the
                PRIMARY badge (a SITREP for an owned incident IS a response document); the machine
                Threat (threat_level_derived) is annotated beneath + the Threat>Response divergence
                cue. Reuses the shipped AnomalyResponseLevelBadge (the incident/anomaly dual-display
                contract) so the whole app reads the two axes in lockstep. Un-anchored / un-pinned
                reports render NOTHING here — absent means absent (never a placeholder §5 level). */}
            {incidentRL && (
              <div className="mt-1.5">
                <AnomalyResponseLevelBadge
                  derived={sitrep.structured_data.threat_level_derived}
                  operatorSet={incidentRL.assessed_level}
                  assessedBy={incidentRL.assessed_by}
                  reason={incidentRL.reason}
                  testidPrefix="sitrep-response-level"
                />
              </div>
            )}
          </div>
          <button
            type="button"
            data-testid="sitrep-close-btn"
            onClick={onClose}
            className="shrink-0 text-slate-400 hover:text-slate-100 text-lg leading-none px-1"
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div
          data-testid="sitrep-body"
          className="flex-1 overflow-y-auto px-5 py-3"
        >
          {renderMarkdown(sitrep.summary_markdown)}
        </div>

        <footer className="flex items-center gap-2 px-5 py-3 border-t border-slate-700 flex-wrap">
          <button
            type="button"
            data-testid="sitrep-copy-btn"
            onClick={copyMarkdown}
            className="text-xs py-1.5 px-3 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-100"
          >
            Copy as markdown
          </button>
          <button
            type="button"
            data-testid="sitrep-download-btn"
            onClick={downloadJson}
            className="text-xs py-1.5 px-3 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-100"
          >
            Download JSON
          </button>
          <DestinationPicker
            value={destinationId}
            onChange={setDestinationId}
            disabled={forwarding}
            testid="sitrep-destination-picker"
          />
          <button
            type="button"
            data-testid="sitrep-forward-btn"
            onClick={forward}
            disabled={forwarding}
            className="text-xs py-1.5 px-3 rounded bg-sky-600/80 hover:bg-sky-600 border border-sky-500 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
          >
            {forwarding ? 'Forwarding…' : 'Forward'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
          >
            Close
          </button>
        </footer>
    </div>
  );

  // Docked (#131): the card is itself the in-flow column — App places it in the
  // region flex row beside the detail panel. Modal: centre it over a backdrop.
  if (docked) return card;
  return (
    <div
      data-testid="sitrep-modal-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 p-6"
    >
      {card}
    </div>
  );
}
